/**
 * Latency: where the seconds go between a customer pressing send and seeing
 * the reply.
 *
 * Every chat turn is traced (see `app.services.latency`): the server times
 * each step of its pipeline on one clock, and the widget times the round trip
 * and the paint on the visitor's clock. This page draws one waterfall bar per
 * message on a shared scale, lists the hops t1–t8 with millisecond times when
 * a row is opened, and summarises each step (p50/p95/avg) over the window.
 *
 * Deployment-wide and admin only, like System check: the rows quote the
 * visitor's words. It has its own site filter instead of the bot switcher.
 */

import { Fragment, useCallback, useEffect, useState } from 'react';

import { api } from '../api/client';
import type { LatencyReport, LatencyStepStats, LatencyTrace } from '../api/types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Spinner,
} from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { useBots } from '../hooks/useBots';
import { botLabel, formatDateTime } from '../lib/format';

const WINDOWS = [
  { hours: 1, label: 'Last hour' },
  { hours: 24, label: 'Last 24 hours' },
  { hours: 24 * 7, label: 'Last 7 days' },
  { hours: 24 * 30, label: 'Last 30 days' },
];

/**
 * The waterfall's six colour groups, in pipeline order. Categorical slots in
 * fixed order, so a group keeps its colour whichever steps a row has.
 */
const GROUPS = {
  network: { label: 'Network', color: '#2a78d6' },
  server: { label: 'Server: database & setup', color: '#eb6834' },
  search: { label: 'Knowledge search', color: '#1baf7a' },
  aiWait: { label: 'AI: first word', color: '#eda100' },
  aiStream: { label: 'AI: writing', color: '#e87ba4' },
  render: { label: 'Widget: on screen', color: '#008300' },
} as const;

type GroupKey = keyof typeof GROUPS;

/** Step columns as the statistics table names them. */
const STEP_LABELS: Record<string, string> = {
  conversation_ms: 'Find or open the conversation',
  persist_visitor_ms: 'Save the customer message',
  history_ms: 'Load chat history',
  retrieval_ms: 'Knowledge search',
  translation_ms: 'Translate and search again (non-English miss)',
  prompt_ms: 'Build the prompt',
  ai_first_token_ms: 'AI: wait for the first word',
  ai_stream_ms: 'AI: stream the rest of the reply',
  persist_reply_ms: 'Save the reply',
  network_ms: 'Network, both directions',
  render_ms: 'Widget: draw the reply on screen',
  server_total_ms: 'Total on the chat server',
  round_trip_ms: 'Total the customer waited (send → reply complete)',
};

interface Segment {
  group: GroupKey;
  label: string;
  ms: number;
}

/**
 * A trace's steps in the order they happened. The network time is measured
 * only as a total (client round trip minus server time), so it is split
 * evenly either side of the server — the same symmetric-path assumption the
 * clock-offset estimate makes.
 */
function segments(trace: LatencyTrace): Segment[] {
  const halfNetwork = trace.network_ms === null ? null : trace.network_ms / 2;
  const steps: [GroupKey, string, number | null][] = [
    ['network', 'Network: customer → server', halfNetwork],
    ['server', STEP_LABELS.conversation_ms, trace.conversation_ms],
    ['server', STEP_LABELS.persist_visitor_ms, trace.persist_visitor_ms],
    ['server', STEP_LABELS.history_ms, trace.history_ms],
    ['search', STEP_LABELS.retrieval_ms, trace.retrieval_ms],
    ['search', STEP_LABELS.translation_ms, trace.translation_ms],
    ['server', STEP_LABELS.prompt_ms, trace.prompt_ms],
    ['aiWait', STEP_LABELS.ai_first_token_ms, trace.ai_first_token_ms],
    ['aiStream', STEP_LABELS.ai_stream_ms, trace.ai_stream_ms],
    ['server', STEP_LABELS.persist_reply_ms, trace.persist_reply_ms],
    ['network', 'Network: server → customer', halfNetwork],
    ['render', STEP_LABELS.render_ms, trace.render_ms],
  ];
  return steps
    .filter((step): step is [GroupKey, string, number] => step[2] !== null)
    .map(([group, label, ms]) => ({ group, label, ms }));
}

/** What the bar spans: the customer's whole wait when reported, else the server's. */
function barTotal(trace: LatencyTrace): number {
  if (trace.round_trip_ms !== null) {
    return trace.round_trip_ms + (trace.render_ms ?? 0);
  }
  return trace.server_total_ms;
}

function formatMs(value: number | null): string {
  if (value === null) return '—';
  return value >= 1000 ? `${(value / 1000).toFixed(2)} s` : `${Math.round(value)} ms`;
}

/** "14:03:07.412" in the viewer's time zone. */
function formatClock(epochMs: number | null): string {
  if (epochMs === null) return '—';
  const date = new Date(epochMs);
  const time = date.toLocaleTimeString(undefined, { hour12: false });
  return `${time}.${String(date.getMilliseconds()).padStart(3, '0')}`;
}

export function Latency(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { bots } = useBots();

  const [hours, setHours] = useState(24);
  const [slug, setSlug] = useState('');
  const [report, setReport] = useState<LatencyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await api.latency({ hours, slug: slug || undefined, limit: 100 }));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load traces');
    } finally {
      setLoading(false);
    }
  }, [hours, slug]);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <Card>
        <EmptyState
          title="Latency is for administrators."
          hint="Its rows quote what visitors typed."
        />
      </Card>
    );
  }

  const scale = Math.max(1, ...(report?.traces ?? []).map(barTotal));

  return (
    <div>
      <PageHeader
        title="Latency"
        description="Every chat message, timed hop by hop in milliseconds: from the customer pressing send to the reply appearing on their screen."
        actions={
          <>
            <select
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              aria-label="Website"
            >
              <option value="">All websites</option>
              {bots.map((bot) => (
                <option key={bot.slug} value={bot.slug}>
                  {botLabel(bot)}
                </option>
              ))}
            </select>
            <select
              value={hours}
              onChange={(event) => setHours(Number(event.target.value))}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              aria-label="Time window"
            >
              {WINDOWS.map((window) => (
                <option key={window.hours} value={window.hours}>
                  {window.label}
                </option>
              ))}
            </select>
            <Button onClick={() => void load()} disabled={loading}>
              Refresh
            </Button>
          </>
        }
      />

      {error && (
        <div className="mb-4">
          <ErrorNotice message={error} />
        </div>
      )}

      {loading && !report ? (
        <Spinner label="Loading traces…" />
      ) : !report || report.traces.length === 0 ? (
        <Card>
          <EmptyState
            title="No messages in this window yet."
            hint="Every chat message is traced from now on. Send one from the Preview tab to see it here."
          />
        </Card>
      ) : (
        <>
          <StatsTable report={report} />

          <Card className="mt-4 p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold">Recent messages</h2>
              <Legend />
            </div>
            <p className="mb-3 text-xs text-slate-500">
              {report.message_path === 'direct' &&
                'The website and its WordPress plugin only load the widget; messages go straight from the widget to the chat server, so there is no website hop to time. '}
              Bars share one scale. Click a row for every timestamp.
            </p>
            <div className="divide-y divide-slate-100">
              {report.traces.map((trace) => (
                <Fragment key={trace.id}>
                  <TraceRow
                    trace={trace}
                    scale={scale}
                    open={openId === trace.id}
                    onToggle={() =>
                      setOpenId((current) => (current === trace.id ? null : trace.id))
                    }
                  />
                  {openId === trace.id && <TraceDetail trace={trace} />}
                </Fragment>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function Legend(): JSX.Element {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
      {Object.values(GROUPS).map((group) => (
        <span key={group.label} className="inline-flex items-center gap-1">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: group.color }}
          />
          {group.label}
        </span>
      ))}
    </div>
  );
}

function StatsTable({ report }: { report: LatencyReport }): JSX.Element {
  const rows: (LatencyStepStats & { total?: boolean })[] = [
    ...report.steps,
    ...report.totals.map((item) => ({ ...item, total: true })),
  ];
  return (
    <Card className="overflow-x-auto p-4">
      <h2 className="mb-1 text-base font-semibold">Where the time goes</h2>
      <p className="mb-3 text-xs text-slate-500">
        Over {report.sample_size.toLocaleString()} messages.
        {report.slowest_step &&
          ` Slowest step (by median): ${STEP_LABELS[report.slowest_step] ?? report.slowest_step}.`}
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
            <th className="py-1.5 pr-3 font-medium">Step</th>
            <th className="px-3 py-1.5 text-right font-medium">Messages</th>
            <th className="px-3 py-1.5 text-right font-medium">Median</th>
            <th className="px-3 py-1.5 text-right font-medium">p95</th>
            <th className="px-3 py-1.5 text-right font-medium">Average</th>
            <th className="py-1.5 pl-3 text-right font-medium">Max</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const slowest = row.key === report.slowest_step;
            return (
              <tr
                key={row.key}
                className={`border-b border-slate-100 ${
                  row.total ? 'font-semibold' : ''
                } ${slowest ? 'bg-amber-50' : ''}`}
              >
                <td className="py-1.5 pr-3">
                  {STEP_LABELS[row.key] ?? row.key}
                  {slowest && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                      slowest
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{row.count}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {formatMs(row.p50_ms)}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {formatMs(row.p95_ms)}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {formatMs(row.avg_ms)}
                </td>
                <td className="py-1.5 pl-3 text-right tabular-nums">
                  {formatMs(row.max_ms)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

function TraceRow({
  trace,
  scale,
  open,
  onToggle,
}: {
  trace: LatencyTrace;
  scale: number;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  const total = barTotal(trace);
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="grid w-full grid-cols-1 gap-1 py-2 text-left hover:bg-slate-50 md:grid-cols-[16rem_1fr_5rem] md:items-center md:gap-3"
    >
      <div className="min-w-0 text-xs">
        <div className="truncate text-slate-800">
          {trace.excerpt ?? '(message removed)'}
        </div>
        <div className="text-slate-500">
          {formatDateTime(trace.created_at)} · {trace.bot_slug}
          {trace.outcome !== 'ai' && ` · ${trace.outcome}`}
          {trace.round_trip_ms === null && ' · server only'}
        </div>
      </div>
      <div className="flex h-4 items-center">
        <div
          className="flex h-3 gap-[2px]"
          style={{ width: `${(total / scale) * 100}%` }}
        >
          {segments(trace).map((segment, index) => (
            <span
              key={index}
              title={`${segment.label}: ${formatMs(segment.ms)}`}
              className="h-full min-w-[2px] first:rounded-l last:rounded-r"
              style={{
                flexGrow: segment.ms,
                flexBasis: 0,
                backgroundColor: GROUPS[segment.group].color,
              }}
            />
          ))}
        </div>
      </div>
      <div className="text-right text-sm font-medium tabular-nums">
        {formatMs(total)}
      </div>
    </button>
  );
}

/**
 * t1–t8 for one message. Server times are the server's clock; widget times
 * are shifted onto it with the estimated offset, so the column reads as one
 * timeline. The "since send" column uses only single-clock measurements
 * except for t3, which needs the one-way estimate.
 */
function TraceDetail({ trace }: { trace: LatencyTrace }): JSX.Element {
  const reported = trace.client_sent_at !== null && trace.round_trip_ms !== null;
  const offset = trace.clock_offset_ms ?? 0;
  const toServer = (value: number | null): number | null =>
    value === null ? null : value + offset;
  // Position of t3 on the "since send" axis: half the network time.
  const uplink = reported && trace.network_ms !== null ? trace.network_ms / 2 : 0;
  const sinceReceived = (epoch: number | null): number | null =>
    epoch === null ? null : uplink + (epoch - trace.server_received_at);

  const firstTokenAt =
    trace.ai_started_at !== null && trace.ai_first_token_ms !== null
      ? trace.ai_started_at + trace.ai_first_token_ms
      : null;

  const hops: [string, string, number | null, number | null, string?][] = [
    [
      't1',
      'Customer pressed send',
      reported ? toServer(trace.client_sent_at) : null,
      reported ? 0 : null,
      reported ? undefined : 'widget did not report (tab closed or old widget)',
    ],
    [
      't2',
      'Website / plugin',
      null,
      null,
      'n/a: the widget talks to the chat server directly',
    ],
    [
      't3',
      'Chat server received',
      trace.server_received_at,
      reported ? uplink : 0,
      reported ? 'one-way time estimated' : undefined,
    ],
    [
      't4',
      'AI request started',
      trace.ai_started_at,
      sinceReceived(trace.ai_started_at),
    ],
    ['', 'AI first word', firstTokenAt, sinceReceived(firstTokenAt)],
    [
      't5',
      'AI reply finished',
      trace.ai_finished_at,
      sinceReceived(trace.ai_finished_at),
    ],
    [
      't6',
      'Chat server sent the reply',
      trace.server_sent_at,
      sinceReceived(trace.server_sent_at),
    ],
    [
      't7',
      'Widget received the reply',
      reported ? toServer(trace.client_reply_at) : null,
      trace.round_trip_ms,
    ],
    [
      't8',
      'Customer sees it',
      reported ? toServer(trace.client_rendered_at) : null,
      trace.round_trip_ms !== null && trace.render_ms !== null
        ? trace.round_trip_ms + trace.render_ms
        : null,
      trace.client_visible === false ? 'tab was in the background' : undefined,
    ],
  ];

  return (
    <div className="overflow-x-auto bg-slate-50 px-3 py-3 text-xs">
      <table className="w-full">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="py-1 pr-3 font-medium">Hop</th>
            <th className="py-1 pr-3 font-medium">Step</th>
            <th className="py-1 pr-3 font-medium">Time (server clock)</th>
            <th className="py-1 pr-3 text-right font-medium">Since send</th>
            <th className="py-1 font-medium">Note</th>
          </tr>
        </thead>
        <tbody>
          {hops.map(([hop, label, at, since, note]) => (
            <tr key={label}>
              <td className="py-0.5 pr-3 font-mono text-slate-500">{hop}</td>
              <td className="py-0.5 pr-3 text-slate-800">{label}</td>
              <td className="py-0.5 pr-3 font-mono tabular-nums">{formatClock(at)}</td>
              <td className="py-0.5 pr-3 text-right font-mono tabular-nums">
                {since === null ? '—' : `+${Math.round(since)} ms`}
              </td>
              <td className="py-0.5 text-slate-500">{note ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-slate-500">
        Provider {trace.provider ?? '—'} · {trace.retrieval_chunks} knowledge chunks
        {trace.translated && ' · translated'} · {trace.reply_chars} characters in{' '}
        {trace.fragments} fragments
        {trace.clock_offset_ms !== null &&
          ` · customer clock ${trace.clock_offset_ms >= 0 ? 'behind' : 'ahead'} by ${Math.abs(trace.clock_offset_ms)} ms (estimated)`}
      </p>
    </div>
  );
}
